import { Activity, ArrowDown, ArrowUp, Clock, DollarSign, Loader2, Radio, Shuffle, SkipForward, User, Users } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchProofOfWorkLive } from '@/api/admin';
import { fmtUsd } from '@/lib/money';

/**
 * Live Engine HUD — admin Proof of Work only (owner request, 28 Sep 2026; R-9 exception 6).
 *
 * Shows which of the four heartbeat steps the engine is on, who is next in each category (exact for
 * FIFO / LIFO; a sample for the two random steps, which draw at heartbeat time), the latest heartbeats
 * with any category skipped because it was empty, and the latest cycles with their payouts.
 *
 * Polls every 10 s (the rest of this page keeps its 60 s cadence) and pauses while the tab is hidden;
 * the server memoises the answer for 3 s, so many open admin tabs cost one build.
 */
const LIVE_REFRESH_MS = 10_000;

const STEPS = {
  solo_random: { n: 1, title: 'Random Solo Earners', sub: '0 upgraded referrals', accent: '#FFC20E', icon: User },
  team_random: { n: 2, title: 'Random Team Builders', sub: '1+ upgraded referrals', accent: '#5EE6A1', icon: Users },
  fifo: { n: 3, title: 'Traditional FIFO', sub: 'oldest timestamp', accent: '#7BC2FF', icon: ArrowUp },
  lifo: { n: 4, title: 'LIFO', sub: 'newest timestamp', accent: '#C29CFF', icon: ArrowDown },
};
const ORDER = ['solo_random', 'team_random', 'fifo', 'lifo'];

const OUTCOME_LABEL = { ran: 'Cycled', idle: 'Idle', failed: 'Failed' };

const cyclesText = (n) => `${n} ${n === 1 ? 'cycle' : 'cycles'}`;

const shortPid = (pid) => (pid ? String(pid).slice(0, 8) : '—');

const timeOf = (iso) => {
  const t = Date.parse(iso || '');
  if (!Number.isFinite(t)) return '—';
  return new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const dateTimeOf = (iso) => {
  const t = Date.parse(iso || '');
  if (!Number.isFinite(t)) return '—';
  return new Date(t).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const StepChip = ({ step, compact }) => {
  const meta = STEPS[step];
  if (!meta) return <span className="text-[var(--sc-text-mute)] italic text-[11px]">not recorded</span>;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap"
      style={{ background: `${meta.accent}1a`, color: meta.accent, border: `1px solid ${meta.accent}44` }}
      title={`Heartbeat ${meta.n}: ${meta.title} (${meta.sub})`}
    >
      HB{meta.n}
      {compact ? null : ` · ${meta.title}`}
    </span>
  );
};

const ReferralTag = ({ status }) => (
  <span
    className="text-[9px] uppercase tracking-wider rounded px-1 py-0.5"
    style={status === 'team' ? { color: '#5EE6A1', background: 'rgba(94,230,161,0.10)' } : { color: '#FFC20E', background: 'rgba(255,194,14,0.10)' }}
  >
    {status === 'team' ? 'Team' : 'Solo'}
  </span>
);

const useCountdown = (iso) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const t = Date.parse(iso || '');
  if (!Number.isFinite(t)) return null;
  return Math.round((t - now) / 1000);
};

const EngineStatus = ({ engine }) => {
  let label = 'Running';
  let color = '#5EE6A1';
  if (!engine.enabled) {
    label = 'Engine Off On This Server';
    color = '#9BA3AE';
  } else if (!engine.import_finalized) {
    label = 'Waiting For Import';
    color = '#FF7A00';
  } else if (!engine.economics_set) {
    label = 'Economics Not Set';
    color = '#FF7A00';
  } else if (engine.paused) {
    label = 'Paused (Pacing 0)';
    color = '#FF7A00';
  }
  return (
    <span className="sc-pill" style={{ padding: '3px 10px', fontSize: 10, color, borderColor: `${color}66` }} data-testid="pow-live-status">
      <span className="sc-dot" style={{ background: color }} />
      {label}
    </span>
  );
};

const StepIndicator = ({ engine, categories }) => {
  const seconds = useCountdown(engine.next_heartbeat_due_at);
  const byStep = Object.fromEntries(categories.map((c) => [c.category, c]));
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="pow-live-steps">
        {ORDER.map((step) => {
          const meta = STEPS[step];
          const Icon = meta.icon;
          const active = engine.current_step === step;
          const c = byStep[step];
          return (
            <div
              key={step}
              className="rounded-xl p-3 transition-all"
              style={{
                background: active ? `${meta.accent}1f` : 'rgba(255,255,255,0.02)',
                border: `1px solid ${active ? meta.accent : 'rgba(255,255,255,0.08)'}`,
                boxShadow: active ? `0 0 0 3px ${meta.accent}22` : 'none',
              }}
              data-testid={`pow-live-step-${step}`}
              aria-current={active ? 'step' : undefined}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] uppercase tracking-[0.2em]" style={{ color: meta.accent }}>
                  Heartbeat {meta.n}
                </span>
                {active ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold" style={{ color: meta.accent }}>
                    <Radio className="w-3 h-3 animate-pulse" /> NEXT UP
                  </span>
                ) : null}
              </div>
              <div className="flex items-center gap-2 mt-1.5">
                <Icon className="w-4 h-4 shrink-0" style={{ color: meta.accent }} />
                <div className="font-display font-bold text-[13px] text-[var(--sc-text)] leading-tight">{meta.title}</div>
              </div>
              <div className="text-[11px] text-[var(--sc-text-mute)] mt-0.5">{meta.sub}</div>
              <div className="text-[11px] text-[var(--sc-text-dim)] mt-2 sc-tabular">
                {c ? `${c.roots_eligible.toLocaleString()} roots · ${c.fillers_eligible.toLocaleString()} fillers` : '—'}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[12px] text-[var(--sc-text-dim)]">
        <span className="inline-flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5 text-[#FFC20E]" />
          Next heartbeat:{' '}
          <span className="sc-tabular text-[var(--sc-text)] font-semibold" data-testid="pow-live-countdown">
            {seconds === null ? 'after the first heartbeat' : seconds > 0 ? `in ${seconds}s` : 'due now'}
          </span>
        </span>
        <span>
          Last: <span className="sc-tabular">{timeOf(engine.last_heartbeat_at)}</span> ({cyclesText(engine.last_heartbeat_cycles)})
        </span>
        <span>
          Next heartbeat sized for <span className="sc-tabular font-semibold text-[var(--sc-text)]">{engine.next_heartbeat_size}</span>{' '}
          {engine.next_heartbeat_size === 1 ? 'cycle' : 'cycles'}
        </span>
        <span>
          Every {engine.interval_seconds}s · pacing {engine.pacing_ratio} · max {engine.max_cycles_per_tick}/heartbeat
        </span>
      </div>
      <div className="text-[11px] text-[var(--sc-text-mute)] leading-[1.55]">
        Each heartbeat starts at the step marked NEXT UP and takes all its roots from that category, topping up from the following steps only if it runs short;
        the step then moves on by one. Fillers rotate the same way on their own, and are paired with roots at random.
      </div>
    </div>
  );
};

const CandidateList = ({ label, rows, random, eligible }) => (
  <div>
    <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)] mb-1.5">
      <span>{label}</span>
      <span className="sc-tabular normal-case tracking-normal">
        {random ? `sample of ${eligible.toLocaleString()}` : `${eligible.toLocaleString()} queued`}
      </span>
    </div>
    {rows.length === 0 ? (
      <div className="text-[11px] text-[var(--sc-text-mute)] italic py-1">Empty — this step would be skipped</div>
    ) : (
      <ol className="space-y-1">
        {rows.map((r, i) => (
          <li key={r.position_id} className="flex items-center justify-between gap-2 text-[11.5px]">
            <span className="flex items-center gap-1.5 min-w-0">
              <span className="text-[var(--sc-text-mute)] sc-tabular w-3 text-right">{random ? '•' : i + 1}</span>
              <span className="text-[var(--sc-text)] font-medium truncate">{r.username || 'unknown'}</span>
              <ReferralTag status={r.referral_status} />
            </span>
            <span className="flex items-center gap-2 shrink-0 text-[10.5px] text-[var(--sc-text-mute)] sc-tabular">
              <span title={r.position_id}>{shortPid(r.position_id)}</span>
              <span>{dateTimeOf(r.purchased_at)}</span>
            </span>
          </li>
        ))}
      </ol>
    )}
  </div>
);

const WhoIsNext = ({ categories, currentStep }) => (
  <div className="grid grid-cols-1 xl:grid-cols-2 gap-3" data-testid="pow-live-next">
    {ORDER.map((step) => {
      const c = categories.find((x) => x.category === step);
      const meta = STEPS[step];
      if (!c) return null;
      return (
        <div
          key={step}
          className="rounded-xl p-3 space-y-3"
          style={{ border: `1px solid ${step === currentStep ? `${meta.accent}88` : 'rgba(255,255,255,0.08)'}`, background: 'rgba(255,255,255,0.015)' }}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="font-display font-bold text-[13px]" style={{ color: meta.accent }}>
              Heartbeat {meta.n}: {meta.title}
            </div>
            {c.random ? (
              <span className="inline-flex items-center gap-1 text-[10px] text-[var(--sc-text-mute)]" title="Picked at random when the heartbeat runs">
                <Shuffle className="w-3 h-3" /> random draw
              </span>
            ) : null}
          </div>
          <CandidateList label="Roots (to be paid)" rows={c.roots} random={c.random} eligible={c.roots_eligible} />
          <CandidateList label="Fillers (to close cycles)" rows={c.fillers} random={c.random} eligible={c.fillers_eligible} />
        </div>
      );
    })}
  </div>
);

const Supplied = ({ list }) =>
  list.length === 0 ? (
    <span className="text-[var(--sc-text-mute)]">—</span>
  ) : (
    <span className="inline-flex flex-wrap gap-1">
      {list
        .filter((c) => c.supplied > 0)
        .map((c) => (
          <span key={c.category} className="inline-flex items-center gap-1">
            <StepChip step={c.category} compact />
            <span className="sc-tabular">×{c.supplied}</span>
          </span>
        ))}
    </span>
  );

const Skipped = ({ list }) =>
  list.length === 0 ? null : (
    <span className="inline-flex flex-wrap items-center gap-1">
      <SkipForward className="w-3 h-3 text-[#FF7A00]" />
      {list.map((s) => (
        <StepChip key={s} step={s} compact />
      ))}
    </span>
  );

const HeartbeatLog = ({ heartbeats }) =>
  heartbeats.length === 0 ? (
    <div className="text-[12px] text-[var(--sc-text-mute)] py-4 text-center">
      No heartbeats recorded yet — they appear here from the first heartbeat after this deploy.
    </div>
  ) : (
    <div className="overflow-x-auto">
      <table className="w-full text-[11.5px]" data-testid="pow-live-heartbeats">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)] border-b border-[rgba(255,182,18,0.10)]">
            <th className="py-2 pr-3 font-medium">Time</th>
            <th className="py-2 pr-3 font-medium">Started At</th>
            <th className="py-2 pr-3 font-medium">Roots From</th>
            <th className="py-2 pr-3 font-medium">Fillers From</th>
            <th className="py-2 pr-3 font-medium">Skipped (Empty)</th>
            <th className="py-2 pr-3 font-medium text-right">Cycles</th>
            <th className="py-2 pr-3 font-medium">Result</th>
          </tr>
        </thead>
        <tbody>
          {heartbeats.map((b) => (
            <tr key={b.at} className="border-b border-[rgba(255,182,18,0.06)] align-top">
              <td className="py-2 pr-3 sc-tabular text-[var(--sc-text-dim)]">{timeOf(b.at)}</td>
              <td className="py-2 pr-3">
                <StepChip step={b.start_step} compact />
              </td>
              <td className="py-2 pr-3">
                <Supplied list={b.roots} />
              </td>
              <td className="py-2 pr-3">
                <Supplied list={b.fillers} />
              </td>
              <td className="py-2 pr-3 space-y-1">
                {b.skipped_roots.length > 0 ? (
                  <div className="flex items-center gap-1">
                    <span className="text-[var(--sc-text-mute)]">roots</span> <Skipped list={b.skipped_roots} />
                  </div>
                ) : null}
                {b.skipped_fillers.length > 0 ? (
                  <div className="flex items-center gap-1">
                    <span className="text-[var(--sc-text-mute)]">fillers</span> <Skipped list={b.skipped_fillers} />
                  </div>
                ) : null}
                {b.skipped_roots.length === 0 && b.skipped_fillers.length === 0 ? <span className="text-[var(--sc-text-mute)]">—</span> : null}
              </td>
              <td className="py-2 pr-3 text-right sc-tabular">
                {b.cycles} / {b.planned}
              </td>
              <td className="py-2 pr-3" title={b.detail || undefined}>
                <span style={{ color: b.outcome === 'failed' ? '#FF7A00' : b.outcome === 'ran' ? '#5EE6A1' : 'var(--sc-text-mute)' }}>
                  {OUTCOME_LABEL[b.outcome] || b.outcome}
                </span>
                {b.outcome === 'idle' && b.planned === 0 ? <span className="text-[var(--sc-text-mute)]"> · not enough fillers</span> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

const CycleLog = ({ cycles }) =>
  cycles.length === 0 ? (
    <div className="text-[12px] text-[var(--sc-text-mute)] py-4 text-center">No cycles yet.</div>
  ) : (
    <div className="space-y-2" data-testid="pow-live-cycles">
      {cycles.map((c) => (
        <div
          key={c.position_id}
          className="rounded-xl p-3 text-[11.5px]"
          style={{ border: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.015)' }}
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2 min-w-0">
              <span className="sc-tabular text-[var(--sc-text-mute)]">{timeOf(c.completed_at)}</span>
              <span className="text-[10px] uppercase tracking-wider text-[var(--sc-text-mute)]">Root</span>
              <span className="font-semibold text-[var(--sc-text)]">{c.username || 'unknown'}</span>
              <span className="sc-tabular text-[var(--sc-text-mute)]" title={c.position_id}>
                {shortPid(c.position_id)}
              </span>
              <StepChip step={c.root_category} />
            </div>
            <div className="flex flex-wrap items-center gap-3 sc-tabular text-[var(--sc-text-dim)]">
              <span className="inline-flex items-center gap-1">
                <DollarSign className="w-3 h-3 text-[#5EE6A1]" />
                cycler {fmtUsd(c.cycler_usd)}
              </span>
              <span title={c.sponsor_orphaned ? 'No payable sponsor: this share went to the house' : undefined}>
                sponsor {fmtUsd(c.sponsor_usd)} {c.sponsor_orphaned ? '→ house' : c.sponsor_username ? `→ ${c.sponsor_username}` : ''}
              </span>
              <span>platform {fmtUsd(c.platform_fee_usd)}</span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <span className="text-[10px] uppercase tracking-wider text-[var(--sc-text-mute)]">Closed by</span>
            {c.fillers.map((f) => (
              <span
                key={f.position_id}
                className="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                <span className="text-[var(--sc-text)]">{f.username || 'unknown'}</span>
                <span className="sc-tabular text-[var(--sc-text-mute)]" title={f.position_id}>
                  {shortPid(f.position_id)}
                </span>
                {f.category ? <StepChip step={f.category} compact /> : null}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

const Section = ({ title, icon: Icon, children, testId }) => (
  <div className="sc-card" data-testid={testId}>
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4 text-[#FFC20E]" />
      <div className="font-display font-bold text-base text-[var(--sc-text)]">{title}</div>
    </div>
    {children}
  </div>
);

const ProofOfWorkLivePanel = () => {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const reqIdRef = useRef(0);

  const load = useCallback(async () => {
    const reqId = ++reqIdRef.current;
    try {
      const d = await fetchProofOfWorkLive();
      if (reqId !== reqIdRef.current) return;
      setData(d);
      setFailed(false);
      setUpdatedAt(Date.now());
    } catch {
      if (reqId === reqIdRef.current) setFailed(true);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, LIVE_REFRESH_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  if (!data) {
    return (
      <div className="sc-card flex items-center gap-2 text-[12px] text-[var(--sc-text-mute)]" data-testid="pow-live-loading">
        {failed ? 'Live engine HUD unavailable — retrying every 10 seconds.' : <Loader2 className="w-4 h-4 animate-spin" />}
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="pow-live-panel">
      <Section title="Live Engine HUD" icon={Activity} testId="pow-live-hud">
        <div className="flex flex-wrap items-center gap-3 mb-4 -mt-1">
          <EngineStatus engine={data.engine} />
          <span className="sc-pill" style={{ padding: '2px 8px', fontSize: 9 }} title="Refreshes every 10 seconds while this tab is visible">
            <span className="sc-dot" />
            live · 10s
          </span>
          <span className="text-[11px] text-[var(--sc-text-mute)] sc-tabular">
            {failed ? 'last refresh failed — showing ' : 'updated '}
            {updatedAt ? new Date(updatedAt).toLocaleTimeString() : ''}
          </span>
          <span className="text-[11px] text-[var(--sc-text-mute)] sc-tabular">
            queue: {data.queue.roots.toLocaleString()} roots · {data.queue.fillers.toLocaleString()} fillers · {data.engine.total_cycles.toLocaleString()}{' '}
            cycles all-time
          </span>
        </div>
        <StepIndicator engine={data.engine} categories={data.categories} />
      </Section>

      <Section title="Who's Next" icon={Users} testId="pow-live-next-section">
        <div className="text-[11px] text-[var(--sc-text-mute)] mb-3 leading-[1.55]">
          FIFO and LIFO show the exact next positions in order. The two random steps are drawn when the heartbeat runs, so they show a random sample of the
          eligible positions (it changes on every refresh) with the total that could be picked.
        </div>
        <WhoIsNext categories={data.categories} currentStep={data.engine.current_step} />
      </Section>

      <Section title="Heartbeat Log" icon={Radio} testId="pow-live-heartbeat-section">
        <HeartbeatLog heartbeats={data.heartbeats} />
      </Section>

      <Section title="Latest Cycles" icon={DollarSign} testId="pow-live-cycle-section">
        <div className="text-[11px] text-[var(--sc-text-mute)] mb-3">
          Cycles completed before this feature was deployed show no heartbeat category (it was not recorded then).
        </div>
        <CycleLog cycles={data.cycles} />
      </Section>
    </div>
  );
};

export default ProofOfWorkLivePanel;
